import type { Messages } from '../catalogue.en'

/**
 * Laboratory: moving between a patient's result surfaces, and everything those
 * surfaces say when they are not showing results.
 *
 * NO COPY HERE RENDERS A VALUE. A result, a unit and a reference range arrive
 * from the laboratory and are shown as they were reported, so nothing in this
 * module may translate one.
 *
 * Composed into the public catalogue by `catalogue.ru.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const laboratory: Partial<Messages> = {
  "lab.correction.reviewTitle": "Исправление подтверждённого лабораторного отчёта",
  "lab.correction.confirmRecords": "Подтверждение сохранит полный исправленный отчёт и вашу причину. Исходные значения останутся в истории.",
  "lab.correction.reason": "Причина исправления",
  "lab.correction.history": "Подтверждённые отчёты и история",
  "lab.correction.sourceChanged": "Этот отчёт уже исправлен или отмечен как ошибочный. Ваш черновик сохранён; откройте актуальную редакцию перед исправлением.",
  "lab.correction.report": "Лабораторный отчёт",
  "lab.correction.recorded": "Записан",
  "lab.correction.status": "Текущий статус",
  "lab.correction.empty": "Нет подтверждённых лабораторных отчётов",
  "lab.correction.recordedOn": "Записан: {when}",
  "lab.correction.collectedOn": "Материал собран: {when}",
  "lab.correction.reportedOn": "Отчёт выдан: {when}",
  "lab.correction.versions": "Редакции отчёта",
  "lab.correction.version": "{revision} · {when} · {status}",
  "lab.correction.seedRollover": "Обновление дат демонстрационных данных",
  "lab.correction.originalValues": "Значения, записанные в этой редакции",
  "lab.correction.sourceEvidence": "Исходные данные документа",
  "lab.correction.start": "Исправить отчёт",
  "lab.correction.resume": "Продолжить исправление {number}",
  "lab.correction.enterError": "Отметить как ошибочный",
  "lab.correction.errorNotice": "Отчёт будет исключён из актуальных результатов и предупреждений. Исходный отчёт и ваша причина останутся в истории.",
  "lab.correction.confirmError": "Подтвердить ошибочный отчёт",
  "lab.correction.cancel": "Отмена",
  "lab.correction.current": "Актуальный клинический отчёт",
  "lab.correction.superseded": "Заменён · только в истории",
  "lab.correction.erroneous": "Ошибочный · только в истории",
  "lab.correction.unknownStatus": "Неизвестный статус · исправление недоступно",
  // The combined workspace name (#194): laboratory results and vital
  // parameters share one section under the same URL identity.
  'lab.title': 'Результаты анализов и витальные показатели',

  'lab.menuLabel': 'Разделы анализов и витальных показателей',
  'lab.navGraphs': 'Графики',
  'lab.navGraphsDescription': 'Динамика результатов во времени',
  'lab.navTable': 'Таблица',
  'lab.navTableDescription': 'Матрица результатов, тест за тестом',
  'lab.navFiles': 'Файлы',
  'lab.navFilesDescription': 'Файлы лабораторных отчётов',
  'lab.report.back': 'Назад',
  'lab.report.enlarge': 'Увеличить документ',
  'lab.navImports': 'Отчёты',
  'lab.navImportsDescription': 'Сохранённые результаты, источники и история версий',

  'lab.forbidden': 'У вас нет прав на просмотр результатов лабораторных исследований и витальных показателей',
  'lab.forbiddenDetail': 'Остальная часть карты пациента по-прежнему доступна.',
  'lab.forbiddenLaboratoryOnly': 'Для этого раздела нужны права на просмотр результатов лабораторных исследований',

  'lab.graphs.listLabel': 'Динамика лабораторных и витальных показателей',
  'lab.files.tableLabel': 'Файлы результатов лабораторных исследований',

  'lab.column.value': 'Значение',
  'lab.column.reference': 'Референсный интервал',
  'lab.column.level': 'Уровень',
  'lab.column.file': 'Файл',
  'lab.column.recorded': 'Записано',

  'lab.graphs.loading': 'Загрузка динамики лабораторных показателей',
  'lab.graphs.loadFailed': 'Не удалось загрузить динамику лабораторных показателей',
  'lab.graphs.empty': 'Нет динамики для построения за выбранный период',
  'lab.files.loading': 'Загрузка файлов результатов',
  'lab.files.loadFailed': 'Не удалось загрузить файлы результатов',
  'lab.files.empty': 'Нет файлов результатов за выбранный период',
  'lab.files.filteredEmpty': 'Нет файлов результатов, соответствующих этим фильтрам',
  'lab.files.filteredEmptyDetail': 'Сбросьте фильтры, чтобы увидеть все файлы результатов за выбранный период.',

  'lab.imports.intro': 'Извлеките обезличенный лабораторный отчёт, сверьте каждое значение с источником, затем подтвердите его в карту пациента.',
  'lab.imports.readOnly': 'Вы можете читать импорты, но не можете их проверять',
  'lab.imports.readOnlyDetail': 'Подтвердить извлечённые результаты должен клиницист с правом записи в лабораторию.',
  'lab.imports.extractFailed': 'Не удалось извлечь документ. Проверьте настроенный ИИ-бэкенд и попробуйте снова.',
  'lab.imports.loading': 'Загрузка лабораторных импортов',
  'lab.imports.unavailable': 'Лабораторные импорты недоступны',
  'lab.imports.empty': 'Лабораторных импортов пока нет',
  'lab.imports.listLabel': 'Лабораторные импорты',
  'lab.imports.unknownLaboratory': 'Лабораторный отчёт',
  'lab.imports.unknownSource': 'Исходный документ',
  'lab.imports.sourceGone': 'Исходный документ больше недоступен',
  'lab.imports.selectionGone': 'Импорт, который вы проверяли, больше не в этом списке.',
  'lab.imports.selectionGoneDetail': 'Выберите один из перечисленных импортов, чтобы продолжить.',
  'lab.imports.analysing': 'Чтение страниц',
  'lab.imports.pagesReview': 'Выбор страниц',
  'lab.imports.recognizing': 'Распознаётся',
  'lab.imports.draft': 'Требует проверки',
  'lab.imports.committed': 'Подтверждён',
  'lab.imports.cancelled': 'Отменён',
  'lab.imports.failed': 'Распознавание не удалось',
  'lab.imports.reviewTitle': 'Проверка клиницистом',
  'lab.imports.openSource': 'Открыть исходный документ',

  'lab.file.title': 'Лабораторные результаты',
  'lab.file.close': 'Закрыть',
  'lab.file.draftWaiting': 'Распознанные значения ждут вашей проверки.',
  'lab.file.saved': 'Результаты из этого отчёта записаны в карту.',
  'lab.file.discarded': 'Предыдущее распознавание этого отчёта было отменено.',
  'lab.file.savedOn': 'Результаты из этого отчёта подтверждены {when}.',
  'lab.file.unavailable': 'Не удалось прочитать черновики лабораторных импортов пациента.',
  'lab.imports.documentPane': 'Исходный документ',
  'lab.imports.valuesPane': 'Распознанные результаты',
  'lab.imports.confirmRecords':
    'Подтверждение сохранит включённые значения с назначенными им датами забора или результата. Они получат отметку о распознавании с этого скана, вашем имени и времени подтверждения.',
  'lab.imports.warnings': 'Предупреждения извлечения',
  'lab.imports.laboratory': 'Лаборатория',
  'lab.imports.collectedAt': 'Дата забора',
  'lab.imports.reportedAt': 'Дата отчёта',
  'lab.imports.include': 'Включить',
  'lab.imports.panel': 'Печатная панель',
  'lab.imports.test': 'Печатный тест',
  'lab.imports.value': 'Печатное значение',
  'lab.imports.unit': 'Печатная единица',
  'lab.imports.reference': 'Референсный интервал',
  'lab.imports.referenceLow': 'Нижняя',
  'lab.imports.referenceHigh': 'Верхняя',
  'lab.imports.interpretation': 'Интерпретация',
  'lab.imports.specimenMethod': 'Образец и метод',
  'lab.imports.specimen': 'Печатный образец',
  'lab.imports.method': 'Печатный метод',
  'lab.imports.coding': 'Код анализа',
  'lab.imports.ucum': 'UCUM',
  'lab.imports.ucumValue': 'Единица UCUM: {code}',
  'lab.imports.notSet': 'Не задано',
  'lab.imports.codingUnmapped': 'Не сопоставлен',
  'lab.imports.addRow': 'Добавить пропущенный результат',
  'lab.imports.save': 'Сохранить проверку',
  'lab.imports.saving': 'Сохранение…',
  'lab.imports.confirm': 'Подтвердить результаты',
  'lab.imports.confirming': 'Подтверждение…',
  'lab.imports.saveFailed': 'Не удалось сохранить проверку. Загрузите последний черновик и попробуйте снова.',
  'lab.imports.confirmFailed': 'Не удалось подтвердить результаты. Заполните обязательные значения и попробуйте снова.',
  'lab.imports.changedConcurrently': 'Этот черновик изменился в другом месте. Перезагрузите его, прежде чем применять правки.',
  'lab.imports.noLongerDraft': 'Этот импорт уже подтверждён и больше не редактируется.',
  'lab.imports.openAIKeyMissing': 'Для этого извлечения нужен ваш ключ OpenAI. Добавьте его в настройках учётной записи.',
  'lab.imports.invalid': 'Проверьте обязательные даты, названия тестов, значения и коды терминологии.',
  'lab.imports.forbidden': 'У вас нет прав на это действие с лабораторным импортом.',
  'lab.imports.gone': 'Этот импорт или его исходный документ больше недоступны.',
  'lab.imports.confirmedNotice': 'Эти проверенные значения записаны как окончательные результаты лабораторных исследований.',

  'lab.omitted.undatedPoints': 'Результаты анализов без даты не показаны на графике ({count})',
  'lab.omitted.emptyValues': 'Результаты анализов без значения не показаны на графике ({count})',
  'lab.omitted.vitalUndated': 'Витальные показатели без даты не показаны на графике ({count})',
  'lab.omitted.vitalTextValues': 'Витальные показатели с текстовым значением не показаны на графике ({count})',

  'lab.partial.laboratory': 'Результаты анализов сейчас недоступны; витальные показатели отображаются.',
  'lab.partial.vitals': 'Витальные показатели сейчас недоступны; результаты анализов отображаются.',

  'lab.criticalResult': 'Критический результат',

  'lab.trend.subtitle': 'Динамика результатов лабораторных исследований и витальных показателей, референсные интервалы и события результатов',
  'lab.trend.summary': 'Сводка графика',
  'lab.trend.indicatorsCount': 'Показателей: {count}',
  'lab.trend.pointsCount': 'Точек: {count}',
  'lab.trend.criticalCount': 'Критических: {count}',
  'lab.trend.refreshing': 'Обновление данных',
  'lab.trend.controls': 'Управление графиком',
  'lab.trend.period': 'Период',
  'lab.trend.presets': 'Быстрый выбор периода',
  'lab.trend.preset7': '7 дн.',
  'lab.trend.preset30': '30 дн.',
  'lab.trend.preset90': '90 дн.',
  'lab.trend.presetAll': 'Все',
  'lab.trend.dateFrom': 'Дата с',
  'lab.trend.dateTo': 'Дата по',
  'lab.trend.resetPeriod': 'Сбросить период',
  'lab.trend.compare': 'Сравнить с предыдущим периодом',
  'lab.trend.compareHint': 'Задайте обе даты периода',
  'lab.trend.comparePrev': 'Предыдущий период',
  'lab.trend.compareLoading': 'Предыдущий период загружается',
  'lab.trend.compareError': 'Предыдущий период недоступен',
  'lab.trend.display': 'Отображение',
  'lab.trend.search': 'Поиск',
  'lab.trend.searchPlaceholder': 'Найти показатель',
  'lab.trend.selectAll': 'Выбрать все',
  'lab.trend.clearSelection': 'Снять выбор',
  'lab.trend.showReference': 'Референсные интервалы',
  'lab.trend.showCritical': 'Критические маркеры',
  'lab.trend.showValues': 'Подписи значений',
  'lab.trend.showEvents': 'События результатов',
  'lab.trend.indicators': 'Показатели',
  'lab.trend.selectedCount': 'Выбрано: {count}',
  'lab.trend.selectionEmpty': 'Выберите показатели для графика',
  'lab.trend.logScale': 'Логарифмическая шкала',
  'lab.trend.logShort': 'лог',
  'lab.trend.criticalMode': 'Стиль критического маркера',
  'lab.trend.critRing': 'Кольцо',
  'lab.trend.critFlag': 'Флаг',
  'lab.trend.critBand': 'Полоса',
  'lab.trend.qualDetected': 'Обнаружено',
  'lab.trend.qualNotDetected': 'Не обнаружено',
  'lab.trend.qualPositive': 'Положительно',
  'lab.trend.qualNegative': 'Отрицательно',
  'lab.trend.qualDoubtful': 'Сомнительно',
  'lab.trend.latest': 'Последнее',
  'lab.trend.latestDate': 'Дата',
  'lab.trend.eventsTitle': 'События результатов',
  'lab.trend.eventCluster': '{date}: {events}',
  'lab.trend.eventSubject': '{kind}, {subject}',
  'lab.trend.eventsCount': 'Событий: {count}',
  'lab.trend.eventsLoading': 'События результатов загружаются',
  'lab.trend.eventsUnavailable': 'События результатов недоступны',
  'lab.trend.legend': 'Легенда отклонений',
  'lab.trend.noGroup': 'Без группы',
  'lab.trend.detailGroup': 'Группа',
  'lab.trend.detailSource': 'Источник даты',
  'lab.trend.sourceItem': 'Дата строки аналита',
  'lab.trend.sourceResult': 'Дата результата',
  'lab.trend.sourceCollection': 'Дата сбора',

  'lab.matrix.title': 'Сводная таблица анализов и витальных показателей',
  // «Показатель», а не «Аналит»: в строках теперь и лабораторные аналиты, и
  // витальные показатели, и заголовок колонки должен быть верен для обоих.
  'lab.matrix.colTest': 'Показатель',
  'lab.matrix.colUnit': 'Единица',
  'lab.matrix.colNorm': 'Норма',
  'lab.matrix.noGroup': 'Без группы',
  'lab.matrix.filters': 'Фильтры сводной таблицы',
  'lab.matrix.dateRange': 'Диапазон дат',
  'lab.matrix.filterTests': 'Показатели',
  'lab.matrix.filterGroups': 'Группы',
  'lab.matrix.filterFamilies': 'Источники',
  'lab.matrix.searchPlaceholder': 'Поиск показателя...',
  'lab.matrix.selectedCount': 'Выбрано: {count}',
  'lab.matrix.criticalOnly': 'Только критические',
  'lab.matrix.dateFrom': 'Дата с',
  'lab.matrix.dateTo': 'Дата по',
  'lab.matrix.reset': 'Сбросить',
  'lab.matrix.empty': 'Анализы не найдены',
  'lab.matrix.undated': 'Без даты',
  'lab.matrix.critical': 'Критическое значение',
  'lab.matrix.recognized': 'Распознано со скана отчёта и подтверждено',
  'lab.matrix.recognizedOn': 'Распознано со скана отчёта, подтверждено {when}',
  'lab.matrix.loading': 'Таблица загружается',
  'lab.matrix.unavailable': 'Таблица недоступна',

  'lab.level.critLow': 'Критически низкое',
  'lab.level.low': 'Низкое',
  'lab.level.normal': 'Норма',
  'lab.level.unknown': 'Отклонение',
  'lab.level.high': 'Высокое',
  'lab.level.critHigh': 'Критически высокое',
  'lab.level.none': 'Без оценки нормы',

  // Витальная половина объединённого раздела (#194).
  'lab.vital.group': 'Витальные показатели',
  'lab.vital.unmapped': 'Не сопоставлено со справочной терминологией',
  'lab.family.laboratory': 'Лаборатория',
  'lab.family.vitalMapped': 'Сопоставленные витальные показатели',
  'lab.family.vitalUnmapped': 'Несопоставленные витальные показатели',
  'lab.vital.detailStatus': 'Статус',
  'lab.vital.detailSourceKind': 'Источник',
  'lab.vital.statusPreliminary': 'Предварительное',
  'lab.vital.statusFinal': 'Окончательное',
  'lab.vital.statusAmended': 'Исправленное',
  'lab.vital.statusCancelled': 'Отменено',
  'lab.vital.statusEnteredInError': 'Внесено по ошибке',
  'lab.vital.sourceManual': 'Введено вручную',
  'lab.vital.sourceDevice': 'Измерено прибором',
  'lab.vital.sourceIntegration': 'Получено из интеграции',
  'lab.vital.sourceDerived': 'Производное значение',
  'lab.vital.sourceSeed': 'Данные начального заполнения',
  'lab.vital.timeSourceSource': 'Время измерения из источника',
  'lab.vital.timeSourceRecorded': 'Время регистрации',
  'lab.vital.timeSourceDerived': 'Выведено из данных источника',
  'lab.vital.timeSourceAbsent': 'В источнике нет даты',

  'lab.status.parsed': 'Разобран',
  'lab.status.superseded': 'Заменён',
  'lab.status.failed': 'Ошибка',

  'lab.valueKind.numeric': 'Числовой',
  'lab.valueKind.qualitative': 'Качественный',
  'lab.valueKind.empty': 'Без значения',

  'lab.event.collected': 'Образец собран',
  'lab.event.received': 'Образец получен',
  'lab.event.finalized': 'Результат утверждён',
  'lab.event.corrected': 'Результат исправлен',
  'lab.event.superseded': 'Результат заменён',
  'lab.event.enteredInError': 'Внесено по ошибке',
}
